import { useContext, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { AuthContext } from "../../../context/AuthContext";
import {
  ConfigProvider,
  Layout,
  Card,
  Row,
  Col,
  Avatar,
  Typography,
  Tag,
  Select,
  Button,
  Progress,
  Space,
  message,
} from "antd";
import { UserOutlined, CheckCircleFilled, ArrowRightOutlined } from "@ant-design/icons";
import ClearanceFormPreview from "./ClearanceFormPreview";
import "./NocFormpage.css";
import FinalSettlementPanel from "../FinalSettlementPanel";

const { Title, Text } = Typography;

// ---------------------------------------------------------------------------
// Config
// NOTE: adjust the "../../../context/AuthContext" import path above to match
// where this file actually lives in your tree (same pattern as HrOneCandidate).
// ---------------------------------------------------------------------------

const EMPLOYEES_PATH = "/api/emp/employees/?status=Confirmed";

// These three match exitprocess/urls.py exactly:
//   path("exit-process/", ExitProcessListCreateView.as_view())      -> EXIT_PROCESS_LIST_PATH
//   path("assign-approvers/", AssignApproversView.as_view())        -> ASSIGN_APPROVERS_PATH
//   path("<int:pk>/clearance-form/", ExitClearanceFormView.as_view()) -> used inside ClearanceFormPreview.jsx
const EXIT_PROCESS_LIST_PATH = "/api/exitprocess/exit-process/";
const ASSIGN_APPROVERS_PATH = "/api/exitprocess/assign-approvers/";

// Fixed department ids for the three cross-functional approvers.
// Department Head is NOT resolved from a department anymore — it's searched
// on demand by employee ID (emp_code), since the pool is the whole company
// (600+ employees) and preloading it all would be slow.
const DEPARTMENT_IDS = {
  hr_id: 25,
  it_id: 98,
  finance_id: 14,
};

const ROLE_META = [
  {
    key: "department_head_id",
    label: "Department Head",
    eyebrow: "01",
    blurb: "Confirms handover of pending work and department-owned items.",
    // No fixed department pool — searched by employee ID (emp_code) on demand.
    remoteSearch: true,
  },
  {
    key: "it_id",
    label: "IT",
    eyebrow: "02",
    blurb: "Clears assets, mail access, internet and application access.",
  },
  {
    key: "finance_id",
    label: "Finance",
    eyebrow: "03",
    blurb: "Settles loans, advances and other full-and-final deductions.",
  },
  {
    key: "hr_id",
    label: "HR",
    eyebrow: "04",
    blurb: "Final clearance — access cards, keys, ID, SIM, exit interview.",
  },
];

// A pine-green identity so this doesn't read as stock antd blue.
const THEME = {
  token: {
    colorPrimary: "#2f5d50",
    colorLink: "#2f5d50",
    borderRadius: 10,
    fontFamily:
      "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  components: {
    Card: { headerFontSize: 16 },
    Tag: { defaultBg: "#f5f6f8" },
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function displayName(emp) {
  if (!emp) return "";
  const parts = [emp.salutation, emp.first_name, emp.last_name].filter(Boolean);
  return parts.join(" ").replace(/\s+/g, " ").trim();
}

function initials(emp) {
  const name = `${emp?.first_name || ""} ${emp?.last_name || ""}`.trim();
  if (!name) return <UserOutlined />;
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0].toUpperCase())
    .join("");
}

// A label-over-value pair used across the ID badge. Each item keeps its own
// column so wrapping text (e.g. a long "Reporting To" name) never drags
// neighbouring items out of alignment the way a shared table row did.
function MetaItem({ label, value, children, mono }) {
  return (
    <div className="meta-item">
      <span className="meta-item-label">{label}</span>
      <span className={`meta-item-value${mono ? " mono" : ""}`}>{children ?? value ?? "—"}</span>
    </div>
  );
}

// Simple debounce hook, used to avoid firing a search request on every
// keystroke when looking up an employee by ID.
function useDebouncedValue(value, delay) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

// ---------------------------------------------------------------------------
// One approver card: search-select an employee from that role's department,
// or show the assigned pill once picked.
// ---------------------------------------------------------------------------

function ApproverCard({ role, employees, loading, value, onChange }) {
  const options = useMemo(
    () =>
      employees.map((emp) => ({
        value: emp.id,
        label: displayName(emp),
        emp,
      })),
    [employees]
  );

  const noDepartment = !role.departmentId;

  return (
    <Card
      className={`approver-card${value ? " approver-card--filled" : ""}`}
      bordered
      title={
        <Space align="start" size={12}>
          <span className="approver-eyebrow">{role.eyebrow}</span>
          <span>
            <div className="approver-card-title">{role.label}</div>
            <Text type="secondary" className="approver-card-blurb">
              {role.blurb}
            </Text>
          </span>
        </Space>
      }
      extra={
        <Tag
          icon={value ? <CheckCircleFilled /> : null}
          color={value ? "success" : "default"}
        >
          {value ? "Assigned" : "Unassigned"}
        </Tag>
      }
    >
      {value ? (
        <div className="approver-assigned">
          <Avatar size={40} src={value.photo} className="approver-assigned-avatar">
            {!value.photo && initials(value)}
          </Avatar>
          <div className="approver-assigned-meta">
            <Text strong>{displayName(value)}</Text>
            <br />
            <Text type="secondary" className="approver-assigned-sub">
              {value.designation_name || "—"} · {value.emp_code || "—"}
            </Text>
          </div>
          <Button type="link" size="small" onClick={() => onChange(null)}>
            Change
          </Button>
        </div>
      ) : noDepartment ? (
        <div className="approver-empty-note">
          Resigning employee has no department set, so {role.label.toLowerCase()} candidates
          can&rsquo;t be looked up.
        </div>
      ) : (
        <Select
          showSearch
          allowClear
          placeholder={`Search employee for ${role.label}`}
          style={{ width: "100%" }}
          loading={loading}
          options={options}
          filterOption={(input, option) => {
            const emp = option.emp;
            const haystack = `${displayName(emp)} ${emp.emp_code || ""} ${emp.department_name || ""} ${emp.designation_name || ""}`.toLowerCase();
            return haystack.includes(input.toLowerCase());
          }}
          optionRender={(option) => {
            const emp = option.data.emp;
            return (
              <Space size={10}>
                <Avatar size={26} src={emp.photo}>
                  {!emp.photo && initials(emp)}
                </Avatar>
                <span>
                  <div>{displayName(emp)}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {emp.designation_name || "—"} · {emp.department_name || "—"} · {emp.emp_code || "—"}
                  </Text>
                </span>
              </Space>
            );
          }}
          onChange={(_, option) => onChange(option ? option.emp : null)}
          notFoundContent={loading ? "Loading employees…" : "No employees in this department."}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Department Head approver card: instead of preloading a department's worth
// of employees, this searches the whole company by employee ID (emp_code),
// on demand, debounced, so we never fetch the full 600+ employee list.
// ---------------------------------------------------------------------------

function DepartmentHeadApproverCard({
  role,
  value,
  onChange,
  backUrl,
  authAccess,
  excludeEmployeeId,
  messageApi,
}) {
  const [searchText, setSearchText] = useState("");
  const [options, setOptions] = useState([]);
  const [searching, setSearching] = useState(false);
  const debouncedSearch = useDebouncedValue(searchText, 400);

  useEffect(() => {
    if (!debouncedSearch.trim() || !authAccess) {
      setOptions([]);
      return;
    }
    let cancelled = false;
    setSearching(true);

    axios
      .get(`${backUrl}${EMPLOYEES_PATH}`, {
        params: { emp_code: debouncedSearch.trim() },
        headers: { Authorization: `Bearer ${authAccess}` },
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        setOptions(list.filter((e) => e.id !== excludeEmployeeId));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Error searching employees by ID:", err);
        messageApi?.error(err.response?.data?.detail || "Could not search employees.");
      })
      .finally(() => !cancelled && setSearching(false));

    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, backUrl, authAccess, excludeEmployeeId, messageApi]);

  const selectOptions = useMemo(
    () => options.map((emp) => ({ value: emp.id, label: displayName(emp), emp })),
    [options]
  );

  return (
    <Card
      className={`approver-card${value ? " approver-card--filled" : ""}`}
      bordered
      title={
        <Space align="start" size={12}>
          <span className="approver-eyebrow">{role.eyebrow}</span>
          <span>
            <div className="approver-card-title">{role.label}</div>
            <Text type="secondary" className="approver-card-blurb">
              {role.blurb}
            </Text>
          </span>
        </Space>
      }
      extra={
        <Tag icon={value ? <CheckCircleFilled /> : null} color={value ? "success" : "default"}>
          {value ? "Assigned" : "Unassigned"}
        </Tag>
      }
    >
      {value ? (
        <div className="approver-assigned">
          <Avatar size={40} src={value.photo} className="approver-assigned-avatar">
            {!value.photo && initials(value)}
          </Avatar>
          <div className="approver-assigned-meta">
            <Text strong>{displayName(value)}</Text>
            <br />
            <Text type="secondary" className="approver-assigned-sub">
              {value.designation_name || "—"} · {value.emp_code || "—"}
            </Text>
          </div>
          <Button type="link" size="small" onClick={() => onChange(null)}>
            Change
          </Button>
        </div>
      ) : (
        <Select
          showSearch
          allowClear
          placeholder="Enter employee ID to search"
          style={{ width: "100%" }}
          loading={searching}
          options={selectOptions}
          filterOption={false} // server-side filtering via emp_code, not client-side
          searchValue={searchText}
          onSearch={setSearchText}
          notFoundContent={
            searching
              ? "Searching…"
              : debouncedSearch.trim()
                ? "No employees found for that ID."
                : "Type an employee ID to search."
          }
          optionRender={(option) => {
            const emp = option.data.emp;
            return (
              <Space size={10}>
                <Avatar size={26} src={emp.photo}>
                  {!emp.photo && initials(emp)}
                </Avatar>
                <span>
                  <div>{displayName(emp)}</div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {emp.designation_name || "—"} · {emp.department_name || "—"} · {emp.emp_code || "—"}
                  </Text>
                </span>
              </Space>
            );
          }}
          onChange={(_, option) => {
            onChange(option ? option.emp : null);
            setSearchText("");
            setOptions([]);
          }}
        />
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export default function NocFormpage({ employee, onAssigned }) {
  const { authData, backUrl } = useContext(AuthContext);

  const [messageApi, contextHolder] = message.useMessage();

  // Department Head is searched on demand by employee ID (no fixed pool).
  // IT / Finance / HR pools are the fixed department ids above.
  const roles = useMemo(
    () =>
      ROLE_META.map((role) => ({
        ...role,
        departmentId: role.remoteSearch ? null : DEPARTMENT_IDS[role.key],
      })),
    []
  );

  const [employeesByRole, setEmployeesByRole] = useState({});
  const [loadingByRole, setLoadingByRole] = useState({});

  const [approvers, setApprovers] = useState({
    department_head_id: null,
    it_id: null,
    finance_id: null,
    hr_id: null,
  });

  const [submitting, setSubmitting] = useState(false);

  // Opened automatically once approvers are assigned successfully.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [exitProcessId, setExitProcessId] = useState(null);
  const [checkingExisting, setCheckingExisting] = useState(true);

  // On load, check whether this employee already has an active exit process
  // (e.g. HR navigated back to this page after assigning approvers earlier)
  // via GET /api/exitprocess/exit-process/?employee=<id>&is_active=true,
  // rather than guessing an id from the employee payload's shape.
  useEffect(() => {
    if (!authData?.access || !employee?.id) {
      setCheckingExisting(false);
      return;
    }
    let cancelled = false;
    setCheckingExisting(true);

    axios
      .get(`${backUrl}${EXIT_PROCESS_LIST_PATH}`, {
        params: { employee: employee.id, is_active: true },
        headers: { Authorization: `Bearer ${authData.access}` },
      })
      .then((res) => {
        if (cancelled) return;
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        if (list.length) {
          // Most recent first (ExitProcess.Meta.ordering = ["-created_at"]).
          setExitProcessId(list[0].id);
        }
      })
      .catch((err) => {
        console.error("Error checking for an existing exit process:", err);
      })
      .finally(() => !cancelled && setCheckingExisting(false));

    return () => {
      cancelled = true;
    };
  }, [authData?.access, backUrl, employee?.id]);

  // Bulk-fetch employees for the fixed-department roles (IT / Finance / HR).
  // Department Head is excluded here — it's searched on demand by employee
  // ID inside DepartmentHeadApproverCard instead.
  useEffect(() => {
    if (!authData?.access) return;
    let cancelled = false;

    async function fetchRoleEmployees(role) {
      if (role.remoteSearch || !role.departmentId) return { key: role.key, list: [] };

      setLoadingByRole((prev) => ({ ...prev, [role.key]: true }));
      try {
        const res = await axios.get(EMPLOYEES_PATH.replace(/^/, backUrl), {
          params: { department: role.departmentId },
          headers: { Authorization: `Bearer ${authData?.access}` },
        });
        const data = res.data;
        const list = Array.isArray(data) ? data : data.results || [];
        return { key: role.key, list: list.filter((e) => e.id !== employee?.id) };
      } catch (err) {
        console.error(`Error fetching ${role.label} employees:`, err);
        if (!cancelled) {
          messageApi.error(
            err.response?.data?.detail || `Failed to fetch ${role.label} employees.`
          );
        }
        return { key: role.key, list: [] };
      } finally {
        if (!cancelled) {
          setLoadingByRole((prev) => ({ ...prev, [role.key]: false }));
        }
      }
    }

    Promise.all(roles.map(fetchRoleEmployees)).then((results) => {
      if (cancelled) return;
      const map = {};
      results.forEach(({ key, list }) => {
        map[key] = list;
      });
      setEmployeesByRole(map);
    });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roles, authData?.access, backUrl, employee?.id]);

  const assignedCount = ROLE_META.filter((r) => approvers[r.key]).length;
  const allAssigned = assignedCount === ROLE_META.length;
  const progressPercent = Math.round((assignedCount / ROLE_META.length) * 100);

  const resignation = employee?.resignation_detail || {};

  async function handleSubmit() {
    setSubmitting(true);
    const payload = {
      employee: employee?.id,
      department_head_id: approvers.department_head_id?.id ?? null,
      it_id: approvers.it_id?.id ?? null,
      finance_id: approvers.finance_id?.id ?? null,
      hr_id: approvers.hr_id?.id ?? null,
    };

    try {
      let exitProcessResult = exitProcessId;
      if (onAssigned) {
        const result = await onAssigned(payload);
        exitProcessResult = result?.id ?? result?.exit_process ?? exitProcessResult;
      } else {
        const res = await axios.post(`${backUrl}${ASSIGN_APPROVERS_PATH}`, payload, {
          headers: { Authorization: `Bearer ${authData?.access}` },
        });
        exitProcessResult = res.data?.id ?? res.data?.exit_process ?? exitProcessResult;
      }
      messageApi.success("Approvers assigned. Clearance is now in progress.");
      setExitProcessId(exitProcessResult);
      setPreviewOpen(true);
    } catch (err) {
      console.error("Error assigning approvers:", err);
      messageApi.error(
        err.response?.data?.detail || "Could not assign approvers. Try again."
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <><ConfigProvider theme={THEME}>
      {contextHolder}
      <Layout className="noc-page">
        <div className="noc-header">
          <Text className="noc-eyebrow">Exit Clearance</Text>
          <Title level={3} className="noc-title">
            Assign Approvers
          </Title>
        </div>

        {/* Employee ID badge */}
        <Card className="id-badge" bordered>
          <Tag className="id-badge-stamp" bordered={false}>
            Exit in progress
          </Tag>

          <div className="id-badge-main">
            <Avatar size={76} shape="square" src={employee?.photo} className="id-badge-photo">
              {!employee?.photo && initials(employee)}
            </Avatar>

            <div className="id-badge-info">
              <Title level={4} className="id-badge-name">
                {displayName(employee) || "Unnamed employee"}
              </Title>
              <Text type="secondary" className="id-badge-role">
                {employee?.designation_name || "—"} · {employee?.department_name || "—"}
              </Text>

              <div className="id-badge-meta">
                <MetaItem label="Employee No." value={employee?.emp_code} mono />
                <MetaItem label="Joining Date" value={formatDate(employee?.date_of_joining)} mono />
                <MetaItem label="Reporting To" value={employee?.reporting_to_name} />
                <MetaItem label="Status">
                  <Tag color="warning">{employee?.status || "—"}</Tag>
                </MetaItem>
              </div>
            </div>
          </div>

          <div className="id-badge-resignation">
            <MetaItem label="Resignation Date" value={formatDate(resignation.date)} mono />
            <MetaItem
              label="Relieving Date"
              value={formatDate(resignation.requested_last_working_day)}
              mono />
            {resignation.reason && <MetaItem label="Reason" value={resignation.reason} />}
          </div>
        </Card>

        {/* Four approver cards */}
        <Row gutter={[20, 20]} className="approver-grid">
          {roles.map((role) => role.remoteSearch ? (
            <Col xs={24} md={12} key={role.key}>
              <DepartmentHeadApproverCard
                role={role}
                value={approvers[role.key]}
                backUrl={backUrl}
                authAccess={authData?.access}
                excludeEmployeeId={employee?.id}
                messageApi={messageApi}
                onChange={(emp) => setApprovers((prev) => ({
                  ...prev,
                  [role.key]: emp,
                }))} />
            </Col>
          ) : (
            <Col xs={24} md={12} key={role.key}>
              <ApproverCard
                role={role}
                employees={employeesByRole[role.key] || []}
                loading={!!loadingByRole[role.key]}
                value={approvers[role.key]}
                onChange={(emp) => setApprovers((prev) => ({
                  ...prev,
                  [role.key]: emp,
                }))} />
            </Col>
          )
          )}
        </Row>

        {/* Footer */}
        <Card className="noc-footer" bordered>
          <Row align="middle" gutter={24} wrap>
            <Col flex="220px">
              <Progress
                percent={progressPercent}
                size="small"
                showInfo={false}
                strokeColor={{ "0%": "#8bbee2", "100%": "#698ba7" }} />
              <Text type="secondary" className="noc-progress-label">
                {assignedCount} of {ROLE_META.length} approvers assigned
              </Text>
            </Col>
            <Col flex="auto" />
            <Col>
              {exitProcessId ? (
                <Button
                  className="noc-preview-link"
                  type="link"
                  loading={checkingExisting}
                  onClick={() => setPreviewOpen(true)}
                >
                  Open clearance form <ArrowRightOutlined />
                </Button>
              ) : null}
              <Button
                type="primary"
                size="large"
                className="noc-assign-btn"
                disabled={!allAssigned}
                loading={submitting}
                onClick={handleSubmit}
              >
                Assign
              </Button>
            </Col>
          </Row>
        </Card>
      </Layout>

      <ClearanceFormPreview
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        exitProcessId={exitProcessId}
        employee={employee}
        resignation={resignation}
        onSaved={() => {
          messageApi.success("Clearance form saved.");
        }} />
    </ConfigProvider>
      {exitProcessId ? (
       <FinalSettlementPanel
          exitProcessId={exitProcessId}
          backUrl={backUrl}
          authAccess={authData?.access}
          employee={employee}
          messageApi={messageApi}
        />
      ) : null}
    </>
  );
}